<script lang="ts">
  import { store } from '$lib/store.svelte';
  import { api } from '$lib/api';
  import { buildReport, toCsv } from '$shared/report';
  import { formatHours, formatMonth, monthRange, today } from '$shared/dates';
  import type { Entry, Report, ReportGrouping } from '$shared/types';

  let clientId = $state(store.activeClientId ?? '');
  let from = $state(monthRange(today()).from);
  let to = $state(monthRange(today()).to);
  let grouping = $state<ReportGrouping>('day');

  let entries = $state<Entry[]>([]);
  let loading = $state(false);
  let publishing = $state(false);
  let publishedUrl = $state<string | null>(null);
  let copied = $state(false);
  let expiresInDays = $state<number | null>(null);

  const client = $derived(store.clients.find((c) => c.id === clientId) ?? null);

  const report = $derived.by((): Report | null =>
    client ? buildReport(client, entries, { from, to }, grouping) : null,
  );

  // Reload whenever the client changes: reports need full history, not just the
  // window the Today screen happens to have cached.
  $effect(() => {
    const id = clientId;
    if (!id) return;
    loading = true;
    store
      .loadAllEntries(id)
      .then((list) => (entries = list))
      .finally(() => (loading = false));
  });

  // A new selection invalidates any link shown for the previous one.
  $effect(() => {
    void [clientId, from, to, grouping];
    publishedUrl = null;
  });

  function setMonth(offset: number) {
    const base = new Date();
    base.setDate(1);
    base.setMonth(base.getMonth() + offset);
    const key = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-01`;
    const range = monthRange(key);
    from = range.from;
    to = range.to;
  }

  function download() {
    if (!report) return;
    const blob = new Blob([toCsv(report)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.clientName.replace(/\W+/g, '-').toLowerCase()}-${from}-to-${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function publish() {
    if (!report) return;
    publishing = true;
    try {
      const result = await api.publishReport(report, expiresInDays ?? undefined);
      publishedUrl = result.url;
    } catch (err) {
      store.error = err instanceof Error ? err.message : String(err);
    } finally {
      publishing = false;
    }
  }

  async function copyLink() {
    if (!publishedUrl) return;
    await navigator.clipboard.writeText(publishedUrl);
    copied = true;
    setTimeout(() => (copied = false), 1600);
  }
</script>

<div class="stack">
  <section class="card stack no-print">
    <h2>Report</h2>

    <div class="grid">
      <div>
        <label for="r-client">Client</label>
        <select id="r-client" bind:value={clientId}>
          <option value="" disabled>Pick a client</option>
          {#each store.clients as c (c.id)}
            <option value={c.id}>{c.name}</option>
          {/each}
        </select>
      </div>
      <div>
        <label for="r-from">From</label>
        <input id="r-from" type="date" bind:value={from} />
      </div>
      <div>
        <label for="r-to">To</label>
        <input id="r-to" type="date" bind:value={to} />
      </div>
      <div>
        <label for="r-group">Group by</label>
        <select id="r-group" bind:value={grouping}>
          <option value="day">Day</option>
          <option value="month">Month</option>
        </select>
      </div>
    </div>

    <div class="row wrap">
      <button onclick={() => setMonth(0)}>This month</button>
      <button onclick={() => setMonth(-1)}>Last month</button>
      <button
        onclick={() => {
          from = `${new Date().getFullYear()}-01-01`;
          to = `${new Date().getFullYear()}-12-31`;
        }}>This year</button
      >
    </div>
  </section>

  {#if loading}
    <p class="muted center">Loading entries…</p>
  {:else if report}
    <section class="card stack">
      <div class="spread no-print">
        <h2>{report.clientName}</h2>
        <div class="row wrap">
          <button onclick={download}>CSV</button>
          <button onclick={() => window.print()}>Print / PDF</button>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{grouping === 'month' ? 'Month' : 'Date'}</th>
              <th class="num">Hours</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {#each report.rows as row (row.key)}
              <tr>
                <td class="key">{grouping === 'month' ? formatMonth(row.key) : row.key}</td>
                <td class="num">{row.hours}</td>
                <td class="notes">{row.notes.join('; ')}</td>
              </tr>
            {:else}
              <tr><td colspan="3" class="muted">No entries in this period.</td></tr>
            {/each}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              <th class="num">{report.totals.hours}</th>
              <td>
                {#if report.totals.amount !== undefined}
                  {report.totals.currency ?? ''}
                  {report.totals.amount.toFixed(2)}
                {/if}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {#if report.retainer}
        <div class="spread retainer">
          <span class="muted">Retainer {report.retainer.size}h</span>
          <strong class="num" class:over={report.retainer.balance < 0}>
            {report.retainer.balance >= 0 ? 'Remaining' : 'Over by'}
            {formatHours(Math.abs(report.retainer.balance))}
          </strong>
        </div>
      {/if}
    </section>

    <section class="card stack no-print">
      <h2>Share with the client</h2>
      <p class="muted fine">
        Publishes a frozen snapshot at a private link — no sign-in needed to view, and later edits
        to your entries will not change what they see.
      </p>
      <div class="row wrap">
        <select bind:value={expiresInDays} aria-label="Link expiry">
          <option value={null}>Never expires</option>
          <option value={7}>Expires in 7 days</option>
          <option value={30}>Expires in 30 days</option>
          <option value={90}>Expires in 90 days</option>
        </select>
        <button class="primary" onclick={publish} disabled={publishing || !report.rows.length}>
          {publishing ? 'Publishing…' : 'Publish link'}
        </button>
      </div>

      {#if publishedUrl}
        <div class="published row">
          <a href={publishedUrl} target="_blank" rel="noreferrer">{publishedUrl}</a>
          <button onclick={copyLink}>{copied ? 'Copied ✓' : 'Copy'}</button>
        </div>
      {/if}
    </section>
  {:else}
    <p class="muted center">Pick a client to build a report.</p>
  {/if}
</div>

<style>
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr));
    gap: 0.75rem;
  }

  .wrap {
    flex-wrap: wrap;
  }

  .center {
    text-align: center;
    padding: 2rem 0;
  }

  .fine {
    font-size: 0.82rem;
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.92rem;
  }

  th,
  td {
    text-align: left;
    padding: 0.45rem 0.6rem;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }

  thead th {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--muted);
  }

  tfoot th,
  tfoot td {
    border-bottom: none;
    border-top: 2px solid var(--line);
    font-weight: 700;
  }

  .key {
    white-space: nowrap;
  }

  .notes {
    color: var(--muted);
  }

  .retainer .over {
    color: var(--danger);
  }

  .published {
    background: var(--surface-2);
    border-radius: var(--radius);
    padding: 0.5rem 0.7rem;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .published a {
    color: var(--accent);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.85rem;
  }

  @media print {
    .no-print {
      display: none !important;
    }

    :global(header.top) {
      display: none !important;
    }

    .table-wrap {
      overflow: visible;
    }
  }
</style>
